/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import Picture from 'src/components/Picture'

const widgetContainer = css`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 8px 16px;
  z-index: 10;

  @media (max-width: 600px) {
    padding: 6px 10px;
    gap: 8px;
  }
`

const teamCell = css`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    gap: 6px;
  }
`

const scoreStyle = css`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.WHITE};
  min-width: 24px;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 18px;
    min-width: 18px;
  }
`

const teamNameStyle = css`
  font-size: 11px;
  color: ${colors.NEUTRAL_50};
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    display: none;
  }
`

const centerCell = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-left: 1px solid ${colors.NEUTRAL_500};
  border-right: 1px solid ${colors.NEUTRAL_500};
  min-width: 80px;

  @media (max-width: 600px) {
    padding: 0 8px;
    min-width: 60px;
  }
`

const timeStyle = css`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.WHITE};

  @media (max-width: 600px) {
    font-size: 12px;
  }
`

const periodStyle = css`
  font-size: 10px;
  font-weight: 500;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const badgeStyle = css`
  display: inline-block;
  background: ${colors.NEUTRAL_600};
  color: ${colors.WHITE};
  font-size: 9px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 2px;
  margin-left: 4px;
  text-transform: uppercase;
`

/**
 * LiveScoreWidget component
 * Compact horizontal widget displaying game score, time, and team info
 * during video playback.
 */
const LiveScoreWidget = ({
  game,
  homeTeamImage,
  visitorTeamImage,
  homeTeamName,
  visitorTeamName,
}) => {
  if (!game) {
    return null
  }

  const { completed, started } = game

  // Determine center content based on game state
  const renderCenterContent = () => {
    if (completed) {
      const isOT = game.isOT || game.overtime
      const isSO = game.isSO || game.shootout
      return (
        <>
          <span css={timeStyle}>
            {t('common:final')}
            {isOT && <span css={badgeStyle}>OT</span>}
            {isSO && <span css={badgeStyle}>SO</span>}
          </span>
        </>
      )
    }

    if (started) {
      return (
        <>
          <span css={timeStyle}>{game.currentPeriodTime || '--:--'}</span>
          <span css={periodStyle}>{game.currentPeriodName || ''}</span>
        </>
      )
    }

    // Not started - show scheduled time
    return (
      <>
        <span css={timeStyle}>{dayjs(game.startsAt).format('h:mm A')}</span>
        <span css={periodStyle}>{dayjs(game.startsAt).format('MMM DD')}</span>
      </>
    )
  }

  return (
    <div css={widgetContainer}>
      {/* Home Team */}
      <div css={teamCell}>
        <Picture
          size='xxsmall'
          square
          fit
          imageId={homeTeamImage}
          iconName='user-friends'
        />
        <span css={scoreStyle}>{game.homeTeamScore ?? 0}</span>
        <span css={teamNameStyle}>{homeTeamName}</span>
      </div>

      {/* Center - Time/Period */}
      <div css={centerCell}>{renderCenterContent()}</div>

      {/* Away Team */}
      <div css={teamCell}>
        <span css={teamNameStyle}>{visitorTeamName}</span>
        <span css={scoreStyle}>{game.visitingTeamScore ?? 0}</span>
        <Picture
          size='xxsmall'
          square
          fit
          imageId={visitorTeamImage}
          iconName='user-friends'
        />
      </div>
    </div>
  )
}

LiveScoreWidget.propTypes = {
  game: PropTypes.object,
  homeTeamImage: PropTypes.string,
  visitorTeamImage: PropTypes.string,
  homeTeamName: PropTypes.string,
  visitorTeamName: PropTypes.string,
}

export default LiveScoreWidget
